import React, { useEffect, useState } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Button } from '@vk-hr-tek/ui/Button';
import { Divider } from '@vk-hr-tek/ui/Divider';
import { Skeleton } from '@vk-hr-tek/ui/Skeleton';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { SurveyAnalyticsQuestion } from '@vk-hr-tek/app-ui/SurveyAnalyticsQuestion';
import { useInject } from '@vk-hr-tek/core/ioc';

import { ContentService, ContentSurveyResultResponse } from '@app/gen/content';

import { ProcessedSurveyPassingItem } from '../../../mappers';
import { SurveyAnalyticsAnswer } from '../../../segments';
import { ContentSurveysItemContainer } from '../ContentSurveysItemContainer';

interface ContentSurveysItemAnalyticsProps {
  survey: ProcessedSurveyPassingItem;
  closeSurveyAnalytics: () => void;
  onDownloadReport: () => void;
}

export const ContentSurveysItemAnalytics = ({
  survey,
  closeSurveyAnalytics,
  onDownloadReport,
}: ContentSurveysItemAnalyticsProps) => {
  const contentService = useInject(ContentService);

  const [answer, setAnswer] = useState<ContentSurveyResultResponse>();
  const [loading, setLoading] = useState(false);

  const canDownloadReport = !!survey.actions?.report;

  useEffect(() => {
    const loadContentSurveyResult = async () => {
      if (survey.id) {
        setLoading(true);

        await contentService
          .getContentSurveyResult({
            params: { content_survey_id: survey.id },
          })
          .then((res) => {
            if (res) {
              setAnswer(res);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    };

    loadContentSurveyResult();
  }, [contentService, survey.id]);

  const getAttachmentUrl = async (attachmentId: string): Promise<string> => {
    const { url } = await contentService.getAttachmentUrl({
      params: { attachment_id: attachmentId },
    });

    return url;
  };

  if (loading)
    return (
      <Box width="100%" height="100%">
        <Skeleton variant="rect" height={336} width="100%" />
      </Box>
    );

  return (
    <ContentSurveysItemContainer survey={survey}>
      <Box display="flex" flexDirection="column" gap="8" py="16">
        {answer?.questions?.map(({ statistics, content, id, skipped }) => (
          <SurveyAnalyticsQuestion
            key={id}
            answerType={statistics.object.answer_type}
            content={content}
            getAttachmentUrl={getAttachmentUrl}
            totalAnswers={statistics.object.total_answers}
            questionSettings={{ required: false, skipped }}
            flat
          >
            <SurveyAnalyticsAnswer
              statistics={statistics.object}
              canDownloadReport={canDownloadReport}
            />
          </SurveyAnalyticsQuestion>
        ))}
      </Box>

      <Divider />

      <Box display="flex" justifyContent="space-between" px="24" py="20">
        <Button
          size="small"
          variant="quaternary"
          onClick={closeSurveyAnalytics}
          startIcon={
            <Icon
              name="arrow_left_outline"
              size="16"
              color="text.light.primary"
            />
          }
        >
          К опросу
        </Button>

        {canDownloadReport && (
          <Tooltip placement="top" title="Скачать отчет (.xlsx)">
            <Button
              size="small"
              onClick={onDownloadReport}
              variant="tertiaryLight"
              startIcon={
                <Icon
                  name="download_outline"
                  size="16"
                  color="text.light.primary"
                />
              }
            >
              Отчет
            </Button>
          </Tooltip>
        )}
      </Box>
    </ContentSurveysItemContainer>
  );
};
